"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FloatingUIWrapper = void 0;
const react_1 = __importStar(require("react"));
const react_2 = require("@floating-ui/react");
const oxygen_config_1 = require("@8x8/oxygen-config");
const IconContainer_1 = require("./IconContainer");
const Button_1 = require("../../../styled/Button");
const SubMenu_1 = require("../../../styled/SubMenu");
/**
 * Renders:
 * - a button with an icon
 * - on hovering over the button, shows a popover on the right, showing children
 */
exports.FloatingUIWrapper = react_1.default.forwardRef(({ icon, header, isActive, testId, children, ...props }, forwardedRef) => {
    const [isOpen, setIsOpen] = (0, react_1.useState)(false);
    const [activeIndex, setActiveIndex] = react_1.default.useState(null);
    const [allowHover, setAllowHover] = react_1.default.useState(false);
    const listItemsRef = react_1.default.useRef([]);
    const arrowRef = (0, react_1.useRef)(null);
    const { theme } = (0, oxygen_config_1.useOxygen)();
    const tree = (0, react_2.useFloatingTree)();
    const { x, y, strategy, refs, context } = (0, react_2.useFloating)({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'right-start',
        middleware: [
            (0, react_2.offset)({ mainAxis: 12, crossAxis: -7 }),
            (0, react_2.shift)(),
            (0, react_2.arrow)({
                element: arrowRef,
            }),
        ],
    });
    const hover = (0, react_2.useHover)(context, {
        enabled: allowHover,
        handleClose: (0, react_2.safePolygon)({
            blockPointerEvents: true,
        }),
    });
    const dismiss = (0, react_2.useDismiss)(context);
    const click = (0, react_2.useClick)(context, {
        event: 'mousedown',
    });
    const listNavigation = (0, react_2.useListNavigation)(context, {
        listRef: listItemsRef,
        activeIndex,
        nested: true,
        onNavigate: setActiveIndex,
        focusItemOnHover: false,
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = (0, react_2.useInteractions)([hover, dismiss, click, listNavigation]);
    // choose either hover or key navigation
    (0, react_1.useEffect)(() => {
        const onPointerMove = ({ pointerType }) => {
            if (pointerType !== 'touch') {
                setAllowHover(true);
                setActiveIndex(null);
            }
        };
        const onKeyDown = () => setAllowHover(false);
        window.addEventListener('pointermove', onPointerMove, {
            once: true,
            capture: true,
        });
        window.addEventListener('keydown', onKeyDown, true);
        return () => {
            window.removeEventListener('pointermove', onPointerMove, {
                capture: true,
            });
            window.removeEventListener('keydown', onKeyDown, true);
        };
    }, [allowHover]);
    // close all menus when an item gets clicked
    (0, react_1.useEffect)(() => {
        if (!tree)
            return;
        const onTreeTrigger = () => setIsOpen(false);
        tree.events.on('trigger', onTreeTrigger);
        // eslint-disable-next-line consistent-return
        return () => {
            tree.events.off('trigger', onTreeTrigger);
        };
    }, [tree]);
    const referenceRef = (0, react_2.useMergeRefs)([refs.setReference, forwardedRef]);
    const hasChildren = react_1.Children.count(children) !== 0;
    return (react_1.default.createElement(react_1.default.Fragment, null,
        isOpen && (react_1.default.createElement(react_2.FloatingFocusManager, { context: context, modal: false, closeOnFocusOut: true },
            react_1.default.createElement(SubMenu_1.FloatingContainer, { ref: refs.setFloating, hasChildren: hasChildren, style: {
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0,
                }, ...getFloatingProps(), ...(0, oxygen_config_1.getTestAttributes)(`${testId}_FLOATING_UI`) },
                react_1.default.createElement(react_2.FloatingArrow, { ref: arrowRef, context: context, height: 7, width: 14, fill: theme.ui06, stroke: theme.ui01, strokeWidth: 1 }),
                header,
                hasChildren && (react_1.default.createElement(SubMenu_1.UlCollapsed, null, react_1.Children.map(children, (child, index) => {
                    const clone = (0, react_1.isValidElement)(child) &&
                        (0, react_1.cloneElement)(child, {
                            ...getItemProps({
                                ref(node) {
                                    listItemsRef.current[index] = node;
                                },
                            }),
                            // the cloned element will have onTrigger passed as props
                            onTrigger() {
                                child.props.onTrigger?.(); // call original element's onTrigger()
                                tree?.events.emit('trigger');
                            },
                        });
                    return clone;
                })))))),
        react_1.default.createElement(Button_1.Button, { ref: referenceRef, ...getReferenceProps(props), isActive: isActive, ...(0, oxygen_config_1.getTestAttributes)(`${testId}_FLOATING_UI_TRIGGER`) },
            react_1.default.createElement(IconContainer_1.IconContainer, { icon: icon }))));
});
exports.FloatingUIWrapper.displayName = 'FloatingUIWrapper';
